/**
 * LearnAndPractice - Educational Games & Practice Library
 * 
 * Editorial-style layout with asymmetric composition,
 * distinctive visual rhythm, and purposeful animations.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { NoteDurationGame } from '../components/Education';
import { TrackSummary, Difficulty } from '../types';
import { useAuth } from '../context/AuthContext';
import { useInView } from '../hooks/useAnimation';
import { API_BASE_URL } from '../config';

type GameMode = 'menu' | 'note-duration' | 'rhythm-flashcards';

// ── Animation Hooks ─────────────────────────────────────────────────────────
function usePageLoadAnimation() {
  const [isLoaded, setIsLoaded] = useState(false);
  useEffect(() => { const t = setTimeout(() => setIsLoaded(true), 100); return () => clearTimeout(t); }, []);
  return isLoaded;
}

// ── SVG Note Illustrations ──────────────────────────────────────────────────
const NoteSVGs = {
  whole: () => (
    <svg viewBox="0 0 100 60" className="w-full h-full" aria-label="Whole Note">
      <ellipse cx="50" cy="30" rx="36" ry="22" stroke="currentColor" strokeWidth="5" fill="none" />
    </svg>
  ),
  half: () => (
    <svg viewBox="0 0 80 140" className="w-full h-full" aria-label="Half Note">
      <ellipse cx="34" cy="106" rx="28" ry="20" stroke="currentColor" strokeWidth="5" fill="none" transform="rotate(-18 34 106)" />
      <line x1="61" y1="98" x2="61" y2="18" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
    </svg>
  ),
  quarter: () => (
    <svg viewBox="0 0 80 140" className="w-full h-full" aria-label="Quarter Note">
      <ellipse cx="34" cy="106" rx="28" ry="20" fill="currentColor" transform="rotate(-18 34 106)" />
      <line x1="61" y1="98" x2="61" y2="18" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
    </svg>
  ),
  eighth: () => (
    <svg viewBox="0 0 90 140" className="w-full h-full" aria-label="Eighth Note">
      <ellipse cx="34" cy="106" rx="28" ry="20" fill="currentColor" transform="rotate(-18 34 106)" />
      <line x1="61" y1="98" x2="61" y2="18" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
      <path d="M61 18 C82 28 86 52 64 66" stroke="currentColor" strokeWidth="5" fill="none" strokeLinecap="round" />
    </svg>
  ),
  sixteenth: () => (
    <svg viewBox="0 0 90 140" className="w-full h-full" aria-label="Sixteenth Note">
      <ellipse cx="34" cy="106" rx="28" ry="20" fill="currentColor" transform="rotate(-18 34 106)" />
      <line x1="61" y1="98" x2="61" y2="14" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
      <path d="M61 14 C82 24 86 46 64 60" stroke="currentColor" strokeWidth="5" fill="none" strokeLinecap="round" />
      <path d="M61 34 C82 44 86 66 64 80" stroke="currentColor" strokeWidth="5" fill="none" strokeLinecap="round" />
    </svg>
  ),
  quarterRest: () => (
    <svg viewBox="0 0 60 120" className="w-full h-full" aria-label="Quarter Rest">
      <path d="M18 14 C38 10 46 22 28 30" stroke="currentColor" strokeWidth="5" fill="none" strokeLinecap="round" />
      <path d="M28 30 L14 62" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
      <path d="M14 62 C22 58 36 64 26 76" stroke="currentColor" strokeWidth="5" fill="none" strokeLinecap="round" />
      <path d="M26 76 C14 84 12 96 22 100 C32 104 40 98 36 108" stroke="currentColor" strokeWidth="5" fill="none" strokeLinecap="round" />
    </svg>
  ),
  eighthRest: () => (
    <svg viewBox="0 0 60 90" className="w-full h-full" aria-label="Eighth Rest">
      <circle cx="42" cy="20" r="12" fill="currentColor" />
      <line x1="34" y1="28" x2="14" y2="74" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
      <line x1="8" y1="74" x2="24" y2="74" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
    </svg>
  ),
};

// ── Flashcard Game ──────────────────────────────────────────────────────────
interface Flashcard {
  Icon: React.FC;
  name: string;
  duration: string;
  description: string;
}

const RhythmFlashcards: React.FC = () => {
  const [current, setCurrent] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [complete, setComplete] = useState(false);

  const cards: Flashcard[] = [
    { Icon: NoteSVGs.whole, name: 'Whole Note', duration: '4 beats', description: 'Hold for 4 counts' },
    { Icon: NoteSVGs.half, name: 'Half Note', duration: '2 beats', description: 'Hold for 2 counts' },
    { Icon: NoteSVGs.quarter, name: 'Quarter Note', duration: '1 beat', description: 'Hold for 1 count' },
    { Icon: NoteSVGs.eighth, name: 'Eighth Note', duration: '½ beat', description: 'Quick note' },
    { Icon: NoteSVGs.sixteenth, name: 'Sixteenth Note', duration: '¼ beat', description: 'Very quick note' },
    { Icon: NoteSVGs.quarterRest, name: 'Quarter Rest', duration: '1 beat', description: 'Silence for 1 count' },
    { Icon: NoteSVGs.eighthRest, name: 'Eighth Rest', duration: '½ beat', description: 'Quick silence' },
  ];

  const next = () => {
    if (current < cards.length - 1) {
      setShowAnswer(false);
      setTimeout(() => setCurrent(c => c + 1), 150);
    } else {
      setComplete(true);
    }
  };

  const prev = () => {
    if (current > 0) {
      setShowAnswer(false);
      setTimeout(() => setCurrent(c => c - 1), 150);
    }
  };

  if (complete) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6">
        <div className="text-center max-w-md animate-[scaleIn_0.5s_ease-out]">
          <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-sage-500/20 flex items-center justify-center text-4xl relative">
            <span>📚</span>
            <div className="absolute inset-0 rounded-full bg-sage-500/20 animate-ping" style={{ animationDuration: '2s' }} />
          </div>
          <h2 className="text-3xl font-display font-bold text-stone-100 mb-2">Study Complete</h2>
          <p className="text-stone-500 mb-8">You reviewed all {cards.length} rhythm symbols</p>
          <button
            onClick={() => { setCurrent(0); setShowAnswer(false); setComplete(false); }}
            className="px-8 py-3 bg-amber-500 text-studio-950 font-semibold rounded-full hover:bg-amber-400 transition-colors"
          >
            Study Again
          </button>
        </div>
      </div>
    );
  }

  const card = cards[current];

  return (
    <div className="min-h-[70vh] flex flex-col px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-12">
        <div>
          <h2 className="text-xl font-bold text-stone-100">Rhythm Flashcards</h2>
          <p className="text-stone-500 text-sm">Learn note symbols</p>
        </div>
        <div className="text-sm">
          <span className="text-amber-500 font-bold">{current + 1}</span>
          <span className="text-stone-500"> / {cards.length}</span>
        </div>
      </div>

      {/* Card */}
      <div className="flex-1 flex items-center justify-center">
        <div 
          className="w-full max-w-md cursor-pointer group"
          onClick={() => setShowAnswer(!showAnswer)}
        >
          <div 
            className={`relative aspect-square rounded-3xl border-2 transition-all duration-500 ${
              showAnswer ? 'border-sage-500/50 bg-studio-900' : 'border-studio-800 bg-studio-900/50 hover:border-amber-500/30'
            }`}
            style={{
              transform: showAnswer ? 'rotateY(180deg)' : 'rotateY(0deg)',
              transformStyle: 'preserve-3d',
            }}
          >
            {/* Front */}
            <div 
              className="absolute inset-0 flex flex-col items-center justify-center p-8"
              style={{ backfaceVisibility: 'hidden' }}
            >
              <div className="w-32 h-32 text-stone-100 mb-4 group-hover:scale-105 transition-transform">
                <card.Icon />
              </div>
              <p className="text-stone-500 text-sm">Click to reveal</p>
            </div>
            
            {/* Back */}
            <div 
              className="absolute inset-0 flex flex-col items-center justify-center p-8"
              style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
            >
              <span className="px-3 py-1 bg-sage-500/20 text-sage-400 text-xs rounded-full mb-4">{card.duration}</span>
              <h3 className="text-2xl font-bold text-amber-500 mb-2">{card.name}</h3>
              <p className="text-stone-400 text-center">{card.description}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-4 mt-8">
        <button onClick={prev} disabled={current === 0} className="px-6 py-3 bg-studio-800 rounded-full disabled:opacity-40 hover:bg-studio-700 transition-colors">
          ← Previous
        </button>
        {showAnswer ? (
          <button onClick={next} className="px-8 py-3 bg-amber-500 text-studio-950 font-semibold rounded-full hover:bg-amber-400 transition-colors">
            {current === cards.length - 1 ? 'Finish' : 'Next →'}
          </button>
        ) : (
          <button onClick={() => setShowAnswer(true)} className="px-8 py-3 bg-sage-500 text-studio-950 font-semibold rounded-full hover:bg-sage-400 transition-colors">
            Show Answer
          </button>
        )}
      </div>

      {/* Progress */}
      <div className="flex justify-center gap-2 mt-6">
        {cards.map((_, i) => (
          <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === current ? 'w-8 bg-amber-500' : i < current ? 'w-1.5 bg-sage-500' : 'w-1.5 bg-studio-800'}`} />
        ))}
      </div>
    </div>
  );
};

// ── Game Selector (Horizontal Scroll) ───────────────────────────────────────
function GameSelector({ onSelect }: { onSelect: (mode: GameMode) => void }) {
  const [ref, inView] = useInView<HTMLDivElement>();
  const [hovered, setHovered] = useState<string | null>(null);

  const games = [
    {
      id: 'note-duration' as const,
      number: '01',
      title: 'Note Duration',
      subtitle: 'Practice',
      description: 'Hold notes for their full value with real piano feedback',
      color: '#f5a623',
      features: ['Interactive', 'Piano Audio'],
    },
    {
      id: 'rhythm-flashcards' as const,
      number: '02',
      title: 'Flashcards',
      subtitle: 'Study',
      description: 'Learn to recognize note and rest symbols at your own pace',
      color: '#81b29a',
      features: ['Self-Paced', '7 Symbols'],
    },
  ];

  return (
    <div ref={ref} className="mb-24">
      {/* Section Header - Left aligned, editorial style */}
      <div className="px-6 mb-8">
        <span 
          className="text-coral-400 text-xs tracking-[0.3em] uppercase"
          style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(10px)', transition: 'all 0.6s ease-out' }}
        >
          Interactive
        </span>
        <h2 
          className="font-display text-3xl font-bold text-stone-100 mt-2"
          style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(10px)', transition: 'all 0.6s ease-out 0.1s' }}
        >
          Choose your exercise
        </h2>
      </div>

      {/* Horizontal Game Cards */}
      <div className="px-6">
        <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {games.map((game, i) => (
            <div
              key={game.id}
              className="flex-shrink-0 w-80 snap-start"
              style={{ 
                opacity: inView ? 1 : 0, 
                transform: inView ? 'translateX(0)' : 'translateX(30px)', 
                transition: `all 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${0.2 + i * 0.1}s` 
              }}
              onMouseEnter={() => setHovered(game.id)}
              onMouseLeave={() => setHovered(null)}
            >
              <button
                onClick={() => onSelect(game.id)}
                className="w-full text-left group"
              >
                {/* Card */}
                <div 
                  className="relative rounded-2xl overflow-hidden transition-all duration-500"
                  style={{
                    background: 'linear-gradient(145deg, #111111 0%, #0a0a0a 100%)',
                    border: `1px solid ${hovered === game.id ? game.color + '50' : '#1f1f20'}`,
                    transform: hovered === game.id ? 'translateY(-4px)' : 'translateY(0)',
                    boxShadow: hovered === game.id ? `0 20px 40px ${game.color}15` : 'none',
                  }}
                >
                  {/* Top accent */}
                  <div className="h-1" style={{ background: game.color, opacity: hovered === game.id ? 1 : 0.3 }} />
                  
                  <div className="p-6">
                    {/* Number */}
                    <span 
                      className="font-display text-5xl font-bold transition-colors duration-300"
                      style={{ color: hovered === game.id ? game.color : '#2a2a2c' }}
                    >
                      {game.number}
                    </span>
                    
                    {/* Title */}
                    <h3 className="text-xl font-bold text-stone-100 mt-4 group-hover:text-amber-500 transition-colors">
                      {game.title}
                    </h3>
                    <p className="text-stone-500 text-sm">{game.subtitle}</p>
                    
                    {/* Description */}
                    <p className="text-stone-400 text-sm mt-4 leading-relaxed">
                      {game.description}
                    </p>
                    
                    {/* Features */}
                    <div className="flex gap-3 mt-6">
                      {game.features.map(f => (
                        <span key={f} className="px-2 py-1 bg-studio-800 text-stone-500 text-xs rounded">
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {/* Bottom action */}
                  <div className="px-6 py-4 border-t border-studio-800 flex items-center justify-between">
                    <span className="text-stone-500 text-sm">Start</span>
                    <span className="text-lg" style={{ color: game.color }}>→</span>
                  </div>
                </div>
              </button>
            </div>
          ))}
          
          {/* Coming Soon Card */}
          <div 
            className="flex-shrink-0 w-64 snap-start"
            style={{ 
              opacity: inView ? 1 : 0, 
              transform: inView ? 'translateX(0)' : 'translateX(30px)', 
              transition: 'all 0.6s cubic-bezier(0.22, 1, 0.36, 1) 0.4s' 
            }}
          >
            <div className="h-full rounded-2xl border border-dashed border-studio-700 flex flex-col items-center justify-center p-6 text-center">
              <span className="text-3xl mb-2">🔜</span>
              <p className="text-stone-500 text-sm">More coming soon</p>
              <p className="text-stone-600 text-xs mt-1">Time signatures • Dotted notes</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Practice Library (Vertical List Layout) ─────────────────────────────────
function PracticeLibrary({ user }: { user: { username: string; is_admin?: boolean } | null }) {
  const navigate = useNavigate();
  const [tracks, setTracks] = useState<TrackSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Difficulty | 'all'>('all');
  const [hoveredTrack, setHoveredTrack] = useState<string | null>(null);
  const [ref, inView] = useInView<HTMLDivElement>();

  const fetchTracks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/tracks`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setTracks(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void fetchTracks(); }, [fetchTracks]);

  const filtered = useMemo(() => 
    filter === 'all' ? tracks : tracks.filter(t => t.difficulty === filter),
    [tracks, filter]
  );

  const diffConfig: Record<string, { color: string; label: string }> = {
    easy: { color: '#81b29a', label: 'Beginner' },
    medium: { color: '#f5a623', label: 'Intermediate' },
    hard: { color: '#e07a5f', label: 'Advanced' },
    expert: { color: '#d4a574', label: 'Expert' },
  };

  return (
    <div ref={ref} className="px-6 pb-24">
      {/* Section Header */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-8">
        <div>
          <span 
            className="text-coral-400 text-xs tracking-[0.3em] uppercase"
            style={{ opacity: inView ? 1 : 0, transition: 'opacity 0.6s' }}
          >
            Library
          </span>
          <h2 
            className="font-display text-3xl font-bold text-stone-100 mt-2"
            style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(10px)', transition: 'all 0.6s ease-out 0.1s' }}
          >
            Practice tracks
          </h2>
        </div>

        {/* Filter Pills */}
        <div className="flex gap-2">
          {(['all', 'easy', 'medium', 'hard', 'expert'] as const).map((d, i) => (
            <button
              key={d}
              onClick={() => setFilter(d)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                filter === d 
                  ? 'bg-amber-500 text-studio-950' 
                  : 'bg-studio-800 text-stone-400 hover:bg-studio-700'
              }`}
              style={{ 
                opacity: inView ? 1 : 0, 
                transform: inView ? 'translateY(0)' : 'translateY(10px)',
                transition: `all 0.4s ease-out ${0.2 + i * 0.05}s` 
              }}
            >
              {d === 'all' ? 'All' : d.charAt(0).toUpperCase() + d.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Tracks List */}
      {loading ? (
        <div className="flex justify-center py-24">
          <div className="w-12 h-12 border-3 border-amber-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="text-center py-24">
          <p className="text-coral-400 mb-4">{error}</p>
          <button onClick={() => void fetchTracks()} className="px-4 py-2 bg-studio-800 rounded-lg hover:bg-studio-700">Retry</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-24 border border-dashed border-studio-700 rounded-2xl">
          <p className="text-stone-500">No tracks found</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((track, i) => {
            const diff = track.difficulty || 'medium';
            const config = diffConfig[diff];
            const isHovered = hoveredTrack === track.id;
            
            return (
              <div
                key={track.id}
                className="group"
                style={{
                  opacity: inView ? 1 : 0,
                  transform: inView ? 'translateX(0)' : 'translateX(-20px)',
                  transition: `all 0.5s cubic-bezier(0.22, 1, 0.36, 1) ${0.1 + i * 0.05}s`
                }}
                onMouseEnter={() => setHoveredTrack(track.id)}
                onMouseLeave={() => setHoveredTrack(null)}
              >
                <button
                  onClick={() => user ? navigate(`/game/${track.id}`) : navigate('/login')}
                  className="w-full text-left"
                >
                  <div 
                    className="flex items-center gap-6 p-4 rounded-xl transition-all duration-300"
                    style={{
                      background: isHovered ? 'rgba(31, 31, 32, 0.8)' : 'transparent',
                      borderLeft: `3px solid ${isHovered ? config.color : 'transparent'}`,
                    }}
                  >
                    {/* Difficulty Indicator */}
                    <div 
                      className="w-2 h-12 rounded-full transition-all duration-300"
                      style={{ 
                        background: config.color,
                        opacity: isHovered ? 1 : 0.3,
                        transform: isHovered ? 'scaleY(1)' : 'scaleY(0.7)'
                      }}
                    />
                    
                    {/* Track Info */}
                    <div className="flex-1 min-w-0">
                      <h3 
                        className="font-bold text-stone-100 truncate transition-colors duration-300"
                        style={{ color: isHovered ? config.color : undefined }}
                      >
                        {track.title}
                      </h3>
                      <p className="text-stone-500 text-sm truncate">{track.artist || 'Unknown'}</p>
                    </div>
                    
                    {/* Difficulty Label */}
                    <span 
                      className="hidden sm:block text-xs px-3 py-1 rounded-full transition-colors duration-300"
                      style={{ 
                        color: config.color,
                        background: isHovered ? `${config.color}15` : 'transparent'
                      }}
                    >
                      {config.label}
                    </span>
                    
                    {/* BPM */}
                    {track.bpm && (
                      <span className="text-stone-500 text-sm font-mono hidden md:block">
                        {Math.round(track.bpm)} BPM
                      </span>
                    )}
                    
                    {/* Arrow */}
                    <span 
                      className="text-xl transition-all duration-300"
                      style={{ 
                        color: isHovered ? config.color : '#3a3a3d',
                        transform: isHovered ? 'translateX(4px)' : 'translateX(0)'
                      }}
                    >
                      →
                    </span>
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Hero Section ────────────────────────────────────────────────────────────
function Hero({ isLoaded }: { isLoaded: boolean }) {
  return (
    <section className="relative min-h-[60vh] flex items-center px-6 py-24 overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-1/2 h-full opacity-30 pointer-events-none">
        <div 
          className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(245, 166, 35, 0.15) 0%, transparent 70%)', filter: 'blur(60px)' }}
        />
      </div>
      
      <div className="max-w-4xl relative">
        {/* Eyebrow */}
        <div 
          className="flex items-center gap-4 mb-6"
          style={{
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.8s cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        >
          <div className="w-12 h-px bg-amber-500" />
          <span className="text-amber-500 text-sm tracking-[0.2em] uppercase">Education</span>
        </div>
        
        {/* Title */}
        <h1 
          className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-stone-100 leading-[0.95] mb-6"
          style={{
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.1s',
          }}
        >
          Learn &<br />
          <span className="italic text-amber-500">Practice</span>
        </h1>
        
        {/* Description */}
        <p 
          className="text-stone-400 text-lg max-w-md leading-relaxed"
          style={{
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.2s',
          }}
        >
          Interactive exercises to build your musical foundation. Master rhythm notation through guided practice.
        </p>
        
        {/* Stats */}
        <div 
          className="flex gap-12 mt-12"
          style={{
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.3s',
          }}
        >
          <div>
            <span className="font-display text-3xl font-bold text-stone-100">2</span>
            <p className="text-stone-500 text-sm mt-1">Interactive Games</p>
          </div>
          <div>
            <span className="font-display text-3xl font-bold text-stone-100">7</span>
            <p className="text-stone-500 text-sm mt-1">Note Symbols</p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Back Button ─────────────────────────────────────────────────────────────
function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <div className="px-6 py-4 sticky top-20 z-30 bg-studio-950/90 backdrop-blur-md border-b border-studio-800/50">
      <button onClick={onClick} className="group flex items-center gap-2 text-stone-400 hover:text-amber-500 transition-colors">
        <span className="transition-transform group-hover:-translate-x-1">←</span>
        <span className="font-medium">Back</span>
      </button>
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────────────────
export const LearnAndPractice: React.FC = () => {
  const [gameMode, setGameMode] = useState<GameMode>('menu');
  const { user } = useAuth();
  const isLoaded = usePageLoadAnimation();

  if (gameMode === 'note-duration') {
    return (
      <div className="min-h-screen bg-studio-950">
        <BackButton onClick={() => setGameMode('menu')} />
        <NoteDurationGame />
      </div>
    );
  }

  if (gameMode === 'rhythm-flashcards') {
    return (
      <div className="min-h-screen bg-studio-950">
        <BackButton onClick={() => setGameMode('menu')} />
        <div className="max-w-2xl mx-auto">
          <RhythmFlashcards />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-studio-950">
      <style>{`
        @keyframes scaleIn {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
      `}</style>
      
      <div className="max-w-5xl mx-auto">
        <Hero isLoaded={isLoaded} />
        
        {/* Divider */}
        <div className="px-6">
          <div className="h-px bg-gradient-to-r from-studio-800 via-studio-700 to-transparent" />
        </div>
        
        <div className="pt-16">
          <GameSelector onSelect={setGameMode} />
        </div>
        
        {/* Divider */}
        <div className="px-6">
          <div className="h-px bg-gradient-to-r from-transparent via-studio-700 to-transparent" />
        </div>
        
        <div className="pt-16">
          <PracticeLibrary user={user} />
        </div>
      </div>
    </div>
  );
};

export default LearnAndPractice;
